import React from 'react';
import { useAppContext } from '@/context/app';
import { fetchWithAuth } from '@/utils/FetchUtils';

type UseApiHook = {
  token?: string;
  apiBase: string;
  buildUri: <Q = Record<string, string>>(path: string, query?: Q, headers?: HeadersInit) => string;
  apiPost: <B = unknown, R = unknown>(path: string, body: B, headers?: HeadersInit) => Promise<R>;
  apiPut: <B = unknown, R = unknown>(path: string, body: B, headers?: HeadersInit) => Promise<R>;
  apiPatch: <B = unknown, R = unknown>(path: string, body: B, headers?: HeadersInit) => Promise<R>;
  apiGet: <R = unknown, Q = Record<string, string>>(
    path: string,
    query?: Q,
    headers?: HeadersInit,
  ) => Promise<R>;
  apiDelete: <R = unknown>(path: string) => Promise<R>;
};
const useApi = (url?: string): UseApiHook => {
  const {
    auth: { user },
  } = useAppContext();
  const token = user?.token;
  const apiBase = url ?? `${import.meta.env.VITE_REST_API_URI}`;

  const buildUri = <Q = Record<string, string>>(path: string, query?: Q): string => {
    const fullUrl = `${apiBase}${path}`;

    const keys: Array<keyof Q> = query ? (Object.keys(query) as Array<keyof Q>) : [];
    if (query && keys.length > 0) {
      const params = new URLSearchParams();

      for (const k of keys) {
        if (query[k]) {
          params.append(String(k), String(query[k]));
        }
      }
      return fullUrl.concat(`?${params.toString()}`);
    }
    return fullUrl;
  };

  const apiPost = React.useCallback(
    <B, R>(path: string, body: B, headers?: HeadersInit): Promise<R> => {
      return new Promise((resolve, reject) => {
        fetchWithAuth(buildUri(path), {
          method: 'POST',
          body: JSON.stringify(body),
          headers: {
            ...(token && { Authorization: `Bearer ${token}` }),
            ...(headers && { ...headers }),
          },
        })
          .then(async resp => {
            if (resp.status === 200 || resp.status === 201) {
              return resolve(await resp.json());
            }
            return reject(new Error(`Status: [${resp.status}] ${await resp.json()}`));
          })
          .catch(err => reject(err));
      });
    },
    [token],
  );

  const apiPut = React.useCallback(
    <B, R>(path: string, body: B, headers?: HeadersInit): Promise<R> => {
      return new Promise((resolve, reject) => {
        fetchWithAuth(buildUri(path), {
          method: 'PUT',
          body: JSON.stringify(body),
          headers: {
            ...(token && { Authorization: `Bearer ${token}` }),
            ...(headers && { ...headers }),
          },
        })
          .then(async resp => {
            if (resp.status === 200 || resp.status === 201) {
              return resolve(await resp.json());
            }
            return reject(new Error(`Status: [${resp.status}] ${await resp.json()}`));
          })
          .catch(err => {
            reject(err);
          });
      });
    },
    [token],
  );

  const apiPatch = React.useCallback(
    <B, R>(path: string, body: B, headers?: HeadersInit): Promise<R> => {
      return new Promise((resolve, reject) => {
        fetchWithAuth(buildUri(path), {
          method: 'PATCH',
          body: JSON.stringify(body),
          headers: {
            ...(token && { Authorization: `Bearer ${token}` }),
            ...(headers && { ...headers }),
          },
        })
          .then(async resp => {
            if (resp.status === 200 || resp.status === 201) {
              return resolve(await resp.json());
            }
            return reject(new Error(`Status: [${resp.status}] ${await resp.json()}`));
          })
          .catch(err => {
            reject(err);
          });
      });
    },
    [token],
  );

  const apiGet = React.useCallback(
    <R, Q = Record<string, string>>(path: string, query?: Q, headers?: HeadersInit): Promise<R> => {
      return new Promise((resolve, reject) => {
        fetchWithAuth(buildUri(path, query), {
          method: 'GET',
          headers: {
            ...(token && { Authorization: `Bearer ${token}` }),
            ...(headers && { ...headers }),
          },
        })
          .then(async resp => {
            if (resp.status === 200 || resp.status === 201) {
              return resolve(await resp.json());
            }
            return reject((await resp.json()));
          })
          .catch(err => reject(err));
      });
    },
    [token],
  );

  const apiDelete = React.useCallback(
    <R>(path: string, headers?: HeadersInit): Promise<R> => {
      return new Promise((resolve, reject) => {
        fetchWithAuth(buildUri(path), {
          method: 'DELETE',
          headers: {
            ...(token && { Authorization: `Bearer ${token}` }),
            ...(headers && { ...headers }),
          },
        })
          .then(async resp => {
            if (resp.status === 200 || resp.status === 201) {
              return resolve(await resp.json());
            }
            return reject(new Error(`Status: [${resp.status}] ${await resp.json()}`));
          })
          .catch(err => reject(err));
      });
    },
    [token],
  );

  return {
    token,
    apiBase,
    buildUri,
    apiPost,
    apiPut,
    apiPatch,
    apiGet,
    apiDelete,
  };
};

export default useApi;
